import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FirebaseContext } from '@/firebase/provider';
import { logAppError } from '@/firebase/error-logger';

/**
 * A ultra-stable, premium error component that displays when the application crashes.
 * It uses a secure Cloud Function to log errors and verify admin permissions,
 * bypassing the potentially corrupted Firestore SDK on the frontend.
 */
export function GlobalErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const [showDetails, setShowDetails] = useState(false);
  const [canShowDetails, setCanShowDetails] = useState(false);

  // 🛡️ SAFE: Access context directly to avoid throwing hooks
  const firebaseContext = useContext(FirebaseContext);
  const functions = firebaseContext?.functions;

  // 🛡️ SAFE: Isolated logging and admin check via Cloud Function
  useEffect(() => {
    if (!functions || !error) return;

    // Small delay to let the initial crash stabilizing before making network calls
    const timer = setTimeout(() => {
      const callLogger = async () => {
        try {
          const errObj = error instanceof Error ? error : new Error(String(error));
          const data = await logAppError(
            functions,
            {
              message: errObj.message,
              stack: errObj.stack || null,
            },
            window.location.pathname
          );
          
          setCanShowDetails(data.shouldDisplayTechnicalData);
        } catch (err) {
          // If the network call fails, we stay in 'safe mode' and hide details
          console.error("GlobalErrorComponent: Failed to log error via Cloud Function", err);
        }
      };
      callLogger();
    }, 200);

    return () => clearTimeout(timer);
  }, [functions, error]);

  // 🛡️ SAFE: Isolated translation check that won't throw or suspend
  const { t: i18nT } = useTranslation(undefined, { useSuspense: false });
  const t = (key: string) => {
    try {
      return i18nT ? i18nT(key) : key;
    } catch (e) {
      return key;
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // 🛡️ SAFE: Serialization that handles circular references and nested objects
  const technicalDetails = (() => {
    try {
      return JSON.stringify(error, Object.getOwnPropertyNames(error || {}), 2);
    } catch (e) {
      return String(error);
    }
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'radial-gradient(circle at top right, #121212, #000000)',
        color: '#FFFFFF',
        p: 3,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 6,
            bgcolor: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Glow */}
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              left: -50,
              width: 150,
              height: 150,
              background: 'rgba(255, 107, 107, 0.1)',
              filter: 'blur(50px)',
              borderRadius: '50%',
            }}
          />

          <Stack spacing={4} alignItems="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 107, 107, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255, 107, 107, 0.2)',
              }}
            >
              <WarningAmberRoundedIcon sx={{ fontSize: 40, color: '#ff6b6b' }} />
            </Box>

            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.02em', color: '#FFFFFF' }}>
                {t('common.error_page.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.6, maxWidth: '400px', mx: 'auto' }}>
                {t('common.error_page.desc')}
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  }
                }}
              >
                {t('common.refresh')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGoHome}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  background: 'linear-gradient(45deg, #FF512F 0%, #DD2476 100%)',
                  boxShadow: '0 4px 14px rgba(255, 81, 47, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF512F 30%, #DD2476 100%)',
                    boxShadow: '0 6px 20px rgba(255, 81, 47, 0.4)',
                  }
                }}
              >
                {t('common.go_home')}
              </Button>
            </Stack>

            {canShowDetails && (
              <Box sx={{ width: '100%' }}>
                <Button
                  size="small"
                  onClick={() => setShowDetails(!showDetails)}
                  sx={{ color: 'rgba(255, 255, 255, 0.3)', textTransform: 'none', fontWeight: 400 }}
                >
                  {showDetails ? t('common.hide_details') : t('common.show_details')}
                </Button>

                {showDetails && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: 3,
                      textAlign: 'left',
                      overflowX: 'auto',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <pre
                      style={{
                        margin: 0,
                        fontSize: '11px',
                        color: '#94a3b8',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {technicalDetails}
                    </pre>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
